import { assetDisplayLabel, buildingRoomDisplay } from '../../../../lib/formatUtils';
import Badge from '../../../../components/ui/Badge';
import PassFailToggle from '../PassFailToggle';

/**
 * Pass/Fail segmented control only — for use in card header (top-right, aligned with Asset ID).
 */
export function RcdPassFailSegment({ assetId, rcdInput, onSetInput }) {
  const isPushButtonFail = rcdInput?.pushButton === 'fail';
  const handleChange = (value) => onSetInput(assetId, { ...rcdInput, pushButton: value });

  return (
    <div className="rcd-segmented-wrap">
      <div className="segmented-control rcd-segmented-compact">
        <button
          type="button"
          className={`segmented-option ${rcdInput?.pushButton === 'pass' ? 'segmented-option-active' : ''}`}
          onClick={() => handleChange('pass')}
        >
          Pass
        </button>
        <button
          type="button"
          className={`segmented-option ${isPushButtonFail ? 'segmented-option-active' : ''}`}
          style={isPushButtonFail ? { backgroundColor: 'var(--danger)', color: 'var(--text-inverse)', borderColor: 'var(--danger)' } : {}}
          onClick={() => handleChange('fail')}
        >
          Fail
        </button>
      </div>
    </div>
  );
}

/**
 * Build Make/Model/Amperage line (e.g. Nhp / Dinsafe / 20A).
 */
function makeModelAmperageLine(asset) {
  const make = (asset.manufacturer ?? asset.make ?? '').trim();
  const model = (asset.model ?? '').trim();
  const cap = asset.extra_details?.capacity ?? asset.rating ?? asset.extra_details?.amperage;
  const amperage = cap != null && cap !== '' ? (String(cap).match(/^\d+(\.\d+)?$/) ? `${cap}A` : String(cap)) : '';
  const parts = [make, model, amperage].filter(Boolean);
  return parts.length ? parts.join(' / ') : null;
}

/**
 * High-density RCD card: Identity, Specs, Labels, Inputs, Action.
 * Building - Room uses buildingRoomDisplay (cleanVal) so "NULL" never appears.
 */
export function RcdCard({
  asset,
  sessionEntry,
  rcdInput,
  onSetInput,
  onEdit,
}) {
  const tested = sessionEntry != null;
  const isSynced = sessionEntry?.synced ?? false;
  const result = sessionEntry?.status ?? 'pass';

  const locationStr = buildingRoomDisplay(asset.building, asset.room);
  const equipmentCode = assetDisplayLabel(asset);
  const specsLine = makeModelAmperageLine(asset);

  const borderClass =
    result === 'fail'
      ? 'rcd-card-border-fail'
      : result === 'pass' && tested
        ? 'rcd-card-border-pass'
        : 'rcd-card-border-default';

  // Send only the field that changed — the store already merges with the
  // existing rcdInputs entry (setRcdInput). Spreading rcdInput here would carry
  // whatever fallback object the caller supplied (e.g. a placeholder push result
  // for an untested RCD) into a field it never touched.
  const handleInputChange = (field, value) => {
    onSetInput(asset.id, { [field]: value });
  };

  const handleAction = (e) => {
    e.stopPropagation();
    onEdit(asset, sessionEntry);
  };

  // Never default an untested RCD to Pass — an unset push-button result must
  // render as genuinely unselected so a tech has to make an explicit choice.
  const displayPush = tested ? result : (rcdInput?.pushButton ?? null);
  const displayFail = displayPush === 'fail';
  const displayTripMs = tested ? (sessionEntry?.tripTimeMs ?? rcdInput?.tripTimeMs ?? '') : (rcdInput?.tripTimeMs ?? '');
  const tripDisabled = tested || displayFail;

  return (
    <div className={`rcd-component-wrapper rcd-test-container asset-card-rcd-inline ${borderClass} rcd-border-mobile-only`}>
      {/* Zone A: Header/Identity + Badge — mobile only (hidden on desktop; row handles ID/Make/Model) */}
      <div className="rcd-mobile-header">
        <div className="rcd-mobile-header-row">
          <div className="rcd-identity-stack">
            {equipmentCode.startsWith('#') ? (
              <span className="asset-card-mobile-id">
                <span className="asset-card-mobile-id-hash">#</span>{equipmentCode.slice(1)}
              </span>
            ) : (
              <span className="asset-card-mobile-id">{equipmentCode}</span>
            )}
            <span className="asset-card-mobile-loc">
              {locationStr || 'Location not set'}
            </span>
            <span className="asset-card-mobile-make-model">
              {specsLine ?? 'No make / model on file'}
            </span>
          </div>
          {tested && (
            <div className="rcd-status-corner">
              {!isSynced ? (
                <Badge variant="warning" className="sync-pending">Saving…</Badge>
              ) : (
                <div className={`rcd-status-badge-pill text-xs ${result === 'pass' ? 'rcd-badge-passed' : 'rcd-badge-failed'}`}>
                  {result === 'pass' ? 'PASSED' : 'FAILED'}
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Zone B: Inputs + Comments — card stack on mobile, inline toolbar on desktop */}
      <div className="rcd-input-zone">
        {tested && (
          <div className="rcd-result-recorded rcd-text-muted text-xs">Result Recorded</div>
        )}
        <div className="rcd-input-group">
          <span className="text-xs rcd-grid-label">Push Button</span>
          <div className="rcd-input-field">
            <PassFailToggle
              value={displayPush}
              onChange={(next) => handleInputChange('pushButton', next)}
              disabled={tested}
              ariaLabel={`Push button result for ${equipmentCode}`}
              className="rcd-push-toggle"
            />
          </div>
          <span className="text-xs rcd-grid-label">Trip Time</span>
          <div className="rcd-input-field rcd-trip-time-wrap">
            <input
              type="number"
              inputMode="decimal"
              min={0}
              step={1}
              placeholder={displayFail ? 'N/A' : '0'}
              className="rcd-trip-time-input rcd-input-field rcd-trip-input"
              value={displayTripMs}
              onChange={tested ? undefined : (e) => handleInputChange('tripTimeMs', e.target.value)}
              disabled={tripDisabled}
              readOnly={tested}
              aria-label="Trip time in milliseconds"
            />
            {!displayFail && <span className="rcd-trip-time-suffix" aria-hidden>ms</span>}
          </div>
        </div>
        <button
          type="button"
          className={`btn btn-sm rcd-action-btn rcd-comments-btn-wrap ${tested ? 'rcd-action-comments rcd-action-comments-edit' : 'rcd-action-comments rcd-comments-btn'}`}
          onClick={handleAction}
          title={tested ? 'Edit comments' : 'Add comments or record defect'}
        >
          {tested ? 'Edit Comment' : 'Comment'}
        </button>
      </div>

    </div>
  );
}
